import { useCallback, useEffect, useMemo, useState } from 'react';
import { PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { adminWriteDenial, canWriteAdminMaster } from '@/components/catalog-admin/admin-write';
import {
  CatalogErrorBlock,
  CatalogMasterCard,
  CatalogStatusBadge,
  ContactGroupIcon,
} from '@/components/catalog-admin/CatalogMasterBits';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly, pluralise } from '@/lib/format';
import type { Ability } from '@/lib/permissions';
import type {
  ContactGroup,
  ContactGroupCreateInput,
  ContactGroupUpdateInput,
  Paginated,
} from '@/lib/types';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';

/* -------------------------------------------------------------------------
 * Contact groups, managed from /catalog.
 *
 * The department a contact sits in — `f_contact_groups`, migration 119, seeded
 * `finance` and `operations`. The read half already existed
 * (`api.listContactGroups`, used by the company form's per-contact picker); the
 * three writes had no screen at all, so until now a third group meant raw SQL.
 *
 * ## Why a card of its own rather than a `MASTERS` entry
 *
 * Same reason the backend keeps it out of its own reference registry: a
 * registry master is exactly `{id, name, sortOrder, isActive}`, and this carries
 * a `slug` with rules of its own — writable on create, refused on update, and
 * the only thing any consumer may resolve a group by. Folding it into
 * `MasterFormModal` would mean a fifteenth kind whose one distinguishing
 * feature is a field that changes behaviour between create and edit, which no
 * other kind there has.
 *
 * ## Loading
 *
 * `perPage: 200` is this endpoint's ceiling (default 50). ⚠️ **`isActive` is
 * deliberately ABSENT** — unfiltered returns retired rows too, which is the
 * whole point of a catalogue screen and exactly what a picker must not do. The
 * company form's picker sends `isActive: true`; this one must not, or the
 * Reactivate control would be offered from nowhere. There is **no `sort`
 * param**: rows arrive `sort_order, name`, already in picker order, so they are
 * rendered as received and never re-sorted here.
 * --------------------------------------------------------------------- */

/** This endpoint's own ceiling: 1..200, default 50. */
const GROUP_QUERY = { page: 1, perPage: 200 } as const;

const SKELETON_ROWS = 4;

/**
 * The server's own slug pattern, byte for byte from `contact-groups.schema.ts`.
 *
 * ⚠️ Mirrored rather than invented. Client-side validation that refuses a value
 * the API accepts is the one thing it must never do, so this is only here
 * because it was measured — and it earns its place by turning a 422 on a field
 * somebody has already tabbed away from into a message beside the box while
 * they are still in it.
 */
const SLUG_PATTERN = /^[a-z][a-z0-9_]*$/;

/** `f_contact_groups.sort_order` is a signed 32-bit INT; negatives are legal. */
const SORT_ORDER_MIN = -2147483648;
const SORT_ORDER_MAX = 2147483647;

/**
 * ⚠️ One object, so `knownKeys` for `routeFieldErrors` is derived from the
 * form's own values and cannot drift from the controls. `slug` is a member on
 * create and is dropped from the routing list on edit — see `knownWriteKeys`.
 */
interface FormValues {
  slug: string;
  name: string;
  sortOrder: string;
  isActive: boolean;
}

function toFormValues(record: ContactGroup | null): FormValues {
  if (!record) return { slug: '', name: '', sortOrder: '', isActive: true };
  return {
    slug: record.slug,
    name: record.name,
    sortOrder: String(record.sortOrder),
    isActive: record.isActive,
  };
}

/**
 * The keys a 422 may be MARKED against, which is not the same as the keys this
 * form renders.
 *
 * ⚠️ **`slug` is rendered on edit and is NOT writable there**, so a refusal
 * naming it must reach the banner rather than the read-only box beside it —
 * marking a control somebody cannot change is a red outline with no action
 * behind it. On create it is a real, writable control and is routed normally.
 */
function knownWriteKeys(isEdit: boolean): string[] {
  return isEdit ? ['name', 'sortOrder', 'isActive'] : ['slug', 'name', 'sortOrder', 'isActive'];
}

interface ListState {
  result: Paginated<ContactGroup> | null;
  error: string | null;
  loading: boolean;
}

export function ContactGroupsCard({ ability }: { ability: Ability }) {
  const [state, setState] = useState<ListState>({ result: null, error: null, loading: true });
  const [nonce, setNonce] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    // Deferred a tick — same reason as every other list on this page.
    const timer = setTimeout(() => {
      setState((previous) => ({ ...previous, loading: true }));
      void api
        .listContactGroups(GROUP_QUERY, signal)
        .then((result) => {
          if (signal.aborted) return;
          setState({ result, error: null, loading: false });
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          setState((previous) => ({
            ...previous,
            error: caught instanceof Error ? caught.message : 'Failed to load contact groups',
          }));
        })
        .finally(() => {
          if (!signal.aborted) setState((previous) => ({ ...previous, loading: false }));
        });
    }, 0);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const rows = state.result?.data ?? [];
  const total = state.result?.meta.total ?? null;
  const retired = rows.filter((row) => !row.isActive).length;

  return (
    <>
      <CatalogMasterCard
        icon={<ContactGroupIcon />}
        title="Contact groups"
        loading={state.loading && state.result === null}
        error={state.error}
        badges={
          <>
            <Badge tone="neutral" variant="outline">
              {pluralise(total ?? 0, 'group')}
            </Badge>
            {retired > 0 && (
              <Badge tone="neutral" variant="outline">
                {retired} retired
              </Badge>
            )}
          </>
        }
        canWrite={canWriteAdminMaster(ability, 'reference-sales')}
        onOpen={() => setOpen(true)}
      />

      {open && (
        <ContactGroupListModal
          onClose={() => setOpen(false)}
          state={state}
          rows={rows}
          ability={ability}
          onReload={reload}
        />
      )}
    </>
  );
}

function ContactGroupListModal({
  onClose,
  state,
  rows,
  ability,
  onReload,
}: {
  onClose: () => void;
  state: ListState;
  rows: ContactGroup[];
  ability: Ability;
  onReload: () => void;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ContactGroup | null>(null);
  const [deleting, setDeleting] = useState<ContactGroup | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * ⚠️ THE ROLE **AND** `reference-data`, in that order — see `admin-write.ts`.
   * Every write on `/contact-groups` is `requireRole(...ADMIN_ROLES)`, which is
   * the guard that actually refuses; the permission is the owner's single
   * catalogue tickbox layered on top of it and can only ever narrow.
   * *SUPERSEDED 2026-09-02: the role alone, on the reading that this endpoint
   * had no permission module.*
   */
  // 2026-10-02: contact groups are `reference-sales`.
  const canWrite = canWriteAdminMaster(ability, 'reference-sales');
  const denial = adminWriteDenial(ability, 'reference-sales');

  const initialLoad = state.loading && state.result === null;
  const columns = 5 + (canWrite ? 1 : 0);

  /**
   * Retire or restore, as a one-key PATCH.
   *
   * ⚠️ `isActive` alone, and nothing else — the same rule every retire on this
   * page follows. Re-sending `name` or `sortOrder` from a row nobody opened is
   * how a retire quietly rewrites a value somebody else changed, and `name` is
   * UNIQUE, so it could also collect a 409 on a field the reader never touched.
   */
  async function setActive(row: ContactGroup, isActive: boolean) {
    if (busy) return;
    setBusy(true);
    setRowError(null);
    try {
      await api.updateContactGroup(row.id, { isActive });
      onReload();
    } catch (caught) {
      setRowError(
        caught instanceof ApiError
          ? caught.status === 403
            ? `${adminWriteDenial(ability, 'reference-sales') ?? 'The server refused that.'} Nothing was changed.`
            : caught.message
          : 'Couldn’t reach the server. Nothing was changed.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleting || busy) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await api.deleteContactGroup(deleting.id);
      setDeleting(null);
      onReload();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        /*
         * ⚠️ Switched on the STATUS, never on the wording — the message carries
         * a count and the backend is free to reword it. It already names how
         * many contacts hold the group and already says to retire instead, so
         * it is rendered as written and the route onward is NAMED rather than
         * restated in different words.
         *
         * ⚠️ This 409 is the ENTIRE guard, which is worth knowing before anyone
         * is tempted to soften the copy: `d_contacts.contact_group_id` is
         * `ON DELETE SET NULL`, not RESTRICT, so without the server's count the
         * delete would succeed and silently un-group every contact somebody had
         * classified — a 204, and nothing to restore from.
         */
        setDeleteError(
          `${caught.message} Close this and use “Retire” on the row instead — every contact in the group keeps resolving its name.`,
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        // Already gone: that is the outcome the user asked for, not an error.
        setDeleting(null);
        onReload();
      } else if (caught instanceof ApiError) {
        setDeleteError(
          caught.status === 403
            ? `${adminWriteDenial(ability, 'reference-sales') ?? 'The server refused that.'} Nothing was deleted.`
            : caught.message,
        );
      } else {
        setDeleteError('Couldn’t reach the server. Nothing was deleted.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Contact groups"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {canWrite && (
              <Button
                variant="primary"
                leadingIcon={<PlusIcon />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                New contact group
              </Button>
            )}
          </>
        }
      >
        <div className="grid gap-3">
          {state.error && (
            <CatalogErrorBlock>Couldn’t load contact groups. {state.error}</CatalogErrorBlock>
          )}

          {rowError && <CatalogErrorBlock>{rowError}</CatalogErrorBlock>}

          {denial && <p className="m-0 text-xs text-fg-subtle">{denial} You can read it here.</p>}

          {initialLoad ? (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              <Table className="min-w-[720px]">
                <ContactGroupHead showActions={canWrite} />
                <TBody>
                  {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                    <TR key={index}>
                      {Array.from({ length: columns }, (_, cell) => (
                        <TD key={cell}>
                          <Skeleton className={cx('h-3', cell === 0 ? 'w-40' : 'w-16')} />
                        </TD>
                      ))}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ContactGroupIcon />}
              title={state.error ? 'Nothing to show' : 'No contact groups configured'}
              description={
                state.error
                  ? 'Couldn’t load the list — see the message above.'
                  : canWrite
                    ? 'Add the first one — the group picker on a company’s contacts has nothing to offer until you do.'
                    : `${denial ?? ''} The group picker on a company’s contacts has nothing to offer.`
              }
            />
          ) : (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              <Table className="min-w-[720px]">
                <ContactGroupHead showActions={canWrite} />
                <TBody>
                  {rows.map((row) => (
                    <TR key={row.id}>
                      <TH scope="row" className="min-w-[9rem]">
                        {/* `break-words`, not `[overflow-wrap:anywhere]` — same
                            column-collapse reasoning as every other name cell
                            on this screen. */}
                        <span className="break-words">{row.name}</span>
                      </TH>

                      {/*
                        * The machine key, and the reason it has a column of its
                        * own: it is what every consumer resolves a group by,
                        * and it is the one value on this row that cannot be
                        * changed after creation. A reader checking whether a
                        * group is the one something matches on needs to see it.
                        */}
                      <TD className="text-xs whitespace-nowrap text-fg-muted tabular">
                        {row.slug}
                      </TD>

                      <TD numeric className="text-xs text-fg-muted tabular">
                        {row.sortOrder}
                      </TD>

                      <TD className="text-xs whitespace-nowrap text-fg-muted">
                        {formatDateOnly(row.updatedAt)}
                      </TD>

                      <TD>
                        <CatalogStatusBadge active={row.isActive} />
                      </TD>

                      {canWrite && (
                        <TD>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconOnly
                              aria-label={`Edit ${row.name}`}
                              title={`Edit ${row.name}`}
                              disabled={busy}
                              leadingIcon={<PencilIcon />}
                              onClick={() => {
                                setEditing(row);
                                setFormOpen(true);
                              }}
                            />
                            {/*
                              * The action almost everyone reaching for "remove"
                              * actually wants, so it reads first and is a
                              * labelled button rather than an icon. Not
                              * confirmed — it is reversible from this very row,
                              * and a confirmation would make the safe path feel
                              * heavier than the dangerous one.
                              */}
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              onClick={() => void setActive(row, !row.isActive)}
                            >
                              <span className="sr-only">
                                {row.isActive ? `Retire ${row.name}` : `Restore ${row.name}`}
                              </span>
                              <span aria-hidden="true">{row.isActive ? 'Retire' : 'Restore'}</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              iconOnly
                              aria-label={`Delete ${row.name} permanently`}
                              title={`Delete ${row.name} permanently`}
                              disabled={busy}
                              leadingIcon={<TrashIcon />}
                              className="text-fg-subtle hover:text-danger"
                              onClick={() => {
                                setDeleteError(null);
                                setDeleting(row);
                              }}
                            />
                          </div>
                        </TD>
                      )}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </div>
      </Modal>

      {formOpen && (
        <ContactGroupFormModal
          record={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            onReload();
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={`Delete “${deleting?.name ?? ''}” permanently?`}
        confirmLabel="Delete group"
        loading={busy}
        error={deleteError}
        description={
          <>
            <p className="m-0">
              The contact group <strong>{deleting?.name}</strong> will be deleted. This can't be
              undone.
            </p>
            <p className="mt-2 mb-0">
              A group that still has contacts in it can't be deleted. Retire it instead, and its
              contacts keep showing the group name.
            </p>
          </>
        }
      />
    </>
  );
}

function ContactGroupHead({ showActions }: { showActions: boolean }) {
  return (
    <THead>
      <TR>
        <TH>Group</TH>
        <TH className="w-44">Slug</TH>
        <TH numeric className="w-20">
          Order
        </TH>
        <TH className="w-28">Updated</TH>
        <TH className="w-28">Status</TH>
        {showActions && (
          <TH className="w-56">
            <span className="sr-only">Actions</span>
          </TH>
        )}
      </TR>
    </THead>
  );
}

/** Create and edit in one form. `record: null` is create. */
function ContactGroupFormModal({
  record,
  onClose,
  onSaved,
}: {
  record: ContactGroup | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = record !== null;
  const [values, setValues] = useState<FormValues>(() => toFormValues(record));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /*
   * Derived from the payload, so "Save is enabled" and "there is something to
   * send" cannot disagree. `null` on create — there is always something to
   * send — and an object on edit, where `{}` is a 422.
   */
  const patch = useMemo(
    () => (record ? buildGroupPatch(values, record) : null),
    [values, record],
  );
  const dirty = patch === null || Object.keys(patch).length > 0;

  function setField<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((previous) => ({ ...previous, [key]: value }));
    setFieldErrors((previous) => {
      if (!(key in previous)) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }

  /**
   * Client-side checks, every one of them mirroring a measured server rule
   * rather than inventing a house limit. Refusing a value the API accepts is
   * the one thing this must never do.
   */
  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};

    if (values.name.trim() === '') errors.name = 'A name is required.';

    if (!isEdit) {
      const slug = values.slug.trim();
      if (slug === '') errors.slug = 'A slug is required, and it cannot be changed later.';
      else if (!SLUG_PATTERN.test(slug)) {
        errors.slug = 'Use lower_snake_case starting with a letter — for example finance_team.';
      }
    }

    const sortOrder = values.sortOrder.trim();
    if (sortOrder !== '') {
      const parsed = Number(sortOrder);
      if (!Number.isInteger(parsed) || parsed < SORT_ORDER_MIN || parsed > SORT_ORDER_MAX) {
        errors.sortOrder = 'Enter a whole number.';
      }
    }

    return errors;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // React propagates by the React tree, so a nested mount would otherwise
    // submit whatever rendered this.
    event.stopPropagation();
    if (saving || !dirty) return;

    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setFormError(null);
      return;
    }

    setSaving(true);
    setFieldErrors({});
    setFormError(null);

    try {
      if (record && patch) {
        await api.updateContactGroup(record.id, patch);
      } else {
        await api.createContactGroup(buildGroupCreate(values));
      }
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 409) {
          /*
           * ⚠️ RENDERED AT FORM LEVEL, NOT PINNED TO A FIELD, and that is the
           * opposite of what every other catalogue form does with a 409.
           *
           * `slug` and `name` are EACH unique, and MySQL's duplicate-key error
           * names the index rather than the column — the service has no reader
           * for it, so the server's sentence deliberately says "that slug or
           * name". Marking one box would be a guess, and half the time it would
           * send somebody to change the field that was fine.
           */
          setFormError(caught.message);
          setFieldErrors({});
        } else if (caught.status === 403) {
          setFormError(
            'Only an administrator can change contact groups. Nothing was saved.',
          );
        } else if (caught.status === 404) {
          setFormError('That contact group no longer exists. Close this and reload the list.');
        } else {
          /*
           * Routed against this form's own write-key list. On edit that list
           * EXCLUDES `slug` even though the box is on screen, because the box
           * is read-only there — see `knownWriteKeys`.
           */
          const routed = caught.routeFieldErrors(knownWriteKeys(isEdit));
          const lines = [...routed.unroutable];
          const generic = caught.message.trim().toLowerCase() === 'validation failed';
          if (!generic && caught.message && !lines.includes(caught.message)) {
            lines.unshift(caught.message);
          }
          setFieldErrors(routed.fields);
          setFormError(lines.length > 0 ? lines.join(' ') : generic ? null : caught.message);
        }
      } else {
        setFormError('Couldn’t reach the server. Nothing was saved.');
      }
    } finally {
      setSaving(false);
    }
  }

  const formId = `contact-group-form-${record?.id ?? 'new'}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit ${record.name}` : 'New contact group'}
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={saving}
            // An empty PATCH is a 422, so on edit there is nothing to send.
            disabled={!dirty}
          >
            {isEdit ? 'Save changes' : 'Create contact group'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="grid gap-4">
        <FormErrorSummary
          formError={formError}
          fieldErrorCount={Object.keys(fieldErrors).length}
        />

        <Field label="Name" required error={fieldErrors.name}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={values.name}
              maxLength={80}
              autoComplete="off"
              invalid={Boolean(fieldErrors.name)}
              onChange={(event) => setField('name', event.target.value)}
            />
          )}
        </Field>

        {/*
          * ⚠️ ONE FIELD, TWO BEHAVIOURS, AND THE EDIT SIDE CARRIES ITS REASON.
          *
          * The `hint` on the read-only branch is the "why a control is
          * disabled" exception to the no-helper-text rule. It is not decoration:
          * the slug is declared on the create schema and ABSENT from the update
          * one, which is `.strict()`, so a form that let somebody type here on
          * edit would collect a 422 with `path: ""` and no control to highlight.
          *
          * The reason the server made it immutable is the half worth showing —
          * a renamed slug disconnects every consumer from the group it names,
          * and the symptom is SILENCE: a filter matching nothing looks exactly
          * like a group nobody is in.
          */}
        {isEdit ? (
          <Field label="Slug">
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={values.slug}
                readOnly
                disabled
                className="tabular"
              />
            )}
          </Field>
        ) : (
          <Field label="Slug" required error={fieldErrors.slug}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={values.slug}
                maxLength={60}
                autoComplete="off"
                className="tabular"
                invalid={Boolean(fieldErrors.slug)}
                onChange={(event) => setField('slug', event.target.value)}
              />
            )}
          </Field>
        )}

        <Field label="Sort order" error={fieldErrors.sortOrder}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="numeric"
              min={SORT_ORDER_MIN}
              max={SORT_ORDER_MAX}
              step={1}
              value={values.sortOrder}
              invalid={Boolean(fieldErrors.sortOrder)}
              onChange={(event) => setField('sortOrder', event.target.value)}
            />
          )}
        </Field>

        <Checkbox
          checked={values.isActive}
          onChange={(event) => setField('isActive', event.target.checked)}
          label="Offered on new contacts"
        />
      </form>
    </Modal>
  );
}

/**
 * Only what changed.
 *
 * ⚠️ **No `slug`.** It is not omitted here as a convention this client keeps —
 * the update schema does not declare it and is `.strict()`, so including it
 * would be a 422 naming a key with no writable control behind it.
 *
 * ⚠️ **An emptied Sort order sends nothing at all**, rather than `0`. The
 * column is NOT NULL with a server default, and `null` is not accepted — so a
 * blank box means "leave it alone", and writing a `0` would silently move a row
 * to the front of every picker that reads this master.
 */
function buildGroupPatch(values: FormValues, record: ContactGroup): ContactGroupUpdateInput {
  const patch: ContactGroupUpdateInput = {};

  const name = values.name.trim();
  if (name !== record.name) patch.name = name;

  const sortOrder = values.sortOrder.trim();
  if (sortOrder !== '' && Number(sortOrder) !== record.sortOrder) {
    patch.sortOrder = Number(sortOrder);
  }

  if (values.isActive !== record.isActive) patch.isActive = values.isActive;

  return patch;
}

/**
 * ⚠️ Built from an explicit key list, never a spread of the form state. The
 * create schema is `.strict()`, so one stray key is a 422 naming it — and
 * `sortOrder` is OMITTED when blank rather than sent as `0`, so the server's
 * own default decides where a new group lands.
 */
function buildGroupCreate(values: FormValues): ContactGroupCreateInput {
  const body: ContactGroupCreateInput = {
    slug: values.slug.trim(),
    name: values.name.trim(),
    isActive: values.isActive,
  };

  const sortOrder = values.sortOrder.trim();
  if (sortOrder !== '') body.sortOrder = Number(sortOrder);

  return body;
}
